/**
 * SettingsAdminView -- admin settings audit (P41.2).
 *
 * Route: /admin/settings
 *
 * Read-only audit of the runtime config the bank is operating under:
 * (a) every MARSAD_* env var the backend reads at boot, grouped by
 *     functional area, with secret-shaped keys masked to a
 *     "configured" / "not set" pill;
 * (b) every app_settings row P37 seeded, sorted by key.
 *
 * No mutation; the editor lands as P41.x backlog. Admin-only.
 */
import type { TFunction } from "i18next";
import { RestoreRecordNote } from "shared/admin/RestoreRecordNote";
import { formatDateTime } from "shared/time/formatDate";
import { i18n } from "shared/i18n";
import { useTranslation } from "react-i18next";

import { envAnchor } from "shared/admin/settingsAnchors";
import { useMemo, useState } from "react";
import { AdminOnlyPanel } from "shared/admin/AdminOnlyPanel";
import { EscalationLine } from "shared/components/EscalationLine";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { MarsadIcon } from "shared/components/MarsadIcon";
import { api } from "shared/api/client";
import { onboardingKeys } from "shared/api/queryClient";
import type { EmailHealth, ProofSigningStatus, RestoreRecord } from "shared/api/types";
import { useAdminSettings } from "shared/api/hooks/useAdminSettings";
import { useAppSettingHistory } from "shared/api/hooks/useAppSettingHistory";
import { useAppSettingPatch } from "shared/api/hooks/useAppSettingPatch";
import { useMe } from "shared/api/useMe";
import { useToast } from "shared/toast/ToastProvider";
import type {
  AppSettingHistoryRow,
  SettingsAppRow,
  SettingsEnvRow,
} from "shared/api/types";

import { Identifier } from "shared/components/Identifier";
interface EnvGroup {
  group: string;
  rows: SettingsEnvRow[];
}

function groupEnvRows(rows: SettingsEnvRow[]): EnvGroup[] {
  const order: string[] = [];
  const byGroup: Record<string, SettingsEnvRow[]> = {};
  for (const row of rows) {
    let bucket = byGroup[row.group];
    if (!bucket) {
      bucket = [];
      byGroup[row.group] = bucket;
      order.push(row.group);
    }
    bucket.push(row);
  }
  return order.map((group) => ({ group, rows: byGroup[group] ?? [] }));
}

function formatAppValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v;
  return JSON.stringify(v);
}

export function SettingsAdminView() {
  const { t } = useTranslation();
  const me = useMe();
  const isAdmin = me.data?.is_admin ?? false;

  const settings = useAdminSettings({ enabled: isAdmin });

  const grouped = useMemo(
    () => groupEnvRows(settings.data?.env ?? []),
    [settings.data],
  );

  if (!isAdmin) {
    return <AdminOnlyPanel surface="Settings" />;
  }

  return (
    <div data-testid="settings-view" className="flex flex-col gap-6 p-4">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold text-slate-900">
          <MarsadIcon
            name="console"
            size={20}
            className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
          />
          {t("screens.settingsAdminView.settingsAudit")}
        </h1>
        <p className="text-xs text-slate-600">
          {t("screens.settingsAdminView.readOnlyViewOfTheRuntimeConfig")}
        </p>
      </header>

      <ResetOnboardingSection />

      <ProofSigningPanel />

      <EmailDeliveryPanel />
      <WebhooksPanel />

      {settings.isLoading && (
        <p data-testid="settings-loading" className="text-xs text-slate-500">
          {t("screens.settingsAdminView.loading")}
        </p>
      )}
      {settings.error && (
        <p data-testid="settings-error" className="text-xs text-rose-700">
          {t("screens.settingsAdminView.failedToLoad")}{" "}
          {settings.error instanceof Error
            ? settings.error.message
            : t("screens.settingsAdminView.unknown")}
        </p>
      )}

      {settings.data && (
        <>
          <EnvSection groups={grouped} restore={settings.data.restore} />
          <AppSettingsSection rows={settings.data.app_settings} />
        </>
      )}
    </div>
  );
}

function EnvSection({
  groups,
  restore,
}: {
  groups: EnvGroup[];
  restore?: RestoreRecord | null | undefined;
}) {
  const { t } = useTranslation();
  /* Round 12 G2, third round for the search. 123 environment rows under
     twenty-three headings, and 131 rows on the page, with no way to
     find one. */
  const [q, setQ] = useState("");
  const needle = q.trim().toLowerCase();
  const shown = needle
    ? groups
        .map((g) => ({
          ...g,
          rows: g.rows.filter(
            (r) =>
              r.key.toLowerCase().includes(needle) ||
              r.description.toLowerCase().includes(needle) ||
              g.group.toLowerCase().includes(needle),
          ),
        }))
        .filter((g) => g.rows.length > 0)
    : groups;
  const matched = shown.reduce((n, g) => n + g.rows.length, 0);
  return (
    <section
      data-testid="settings-env-section"
      className="rounded border border-slate-200 bg-white"
    >
      <header className="border-b border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700">
        {t("screens.settingsAdminView.marsadEnvironmentVariables")}
      </header>
      {/* G2 -- what a value on this page MEANS. The round whose own
          test exists because fifty-seven settings reached nothing is
          the round on which this console still could not say, for any
          row, whether the value reached the container or the code's
          default is in force. */}
      <p
        data-testid="settings-env-legend"
        className="border-b border-slate-100 px-3 py-2 text-[11px] text-slate-500"
      >
        {t("screens.settingsAdminView.envLegend")}
      </p>
      <div className="border-b border-slate-100 px-3 py-2">
        <input
          type="search"
          /* ROUND 13 H -- the fix for the three-round-old complaint
             arrived without a label, leaving the one unnamed field on
             this console. A placeholder is not a name: it is not read
             as one by every assistive technology and it disappears the
             moment somebody types (the standard round 10 F2 set). */
          aria-label={t("screens.settingsAdminView.envSearchLabel")}
          data-testid="settings-env-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("screens.settingsAdminView.envSearchPlaceholder")}
          className="w-full max-w-sm rounded border border-slate-300 px-2 py-1 text-xs"
        />
        {needle && (
          <span
            data-testid="settings-env-search-count"
            className="ms-2 text-[11px] text-slate-500"
          >
            {t("screens.settingsAdminView.envSearchCount", { count: matched })}
          </span>
        )}
      </div>
      {shown.map(({ group, rows }) => (
        <div
          key={group}
          /* Round 11 C5 -- the app-settings groups have carried an
             anchor since round 10 and the env groups did not, so a
             surface naming a figure had nowhere to send a reader for
             the half of them that live in the environment. Derived
             from the group name rather than curated: a second list to
             keep in step is a second list to fall out of step. */
          id={envAnchor(group)}
          data-testid="settings-env-group"
          data-group={group}
          className="scroll-mt-4 border-b border-slate-100 last:border-b-0"
        >
          <h2 className="bg-slate-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            {catalogued(t, `settingsCatalogue.envGroup.${envGroupSlug(group)}`, group)}
          </h2>
          {/* Round 16 K12 -- the product's own record beside the typed line. */}
          {group === "Deployment" && (
            <div className="border-b border-slate-50 px-3 py-2">
              <RestoreRecordNote restore={restore} />
            </div>
          )}
          <table className="w-full text-xs">
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.key}
                  data-testid="settings-env-row"
                  data-env-key={row.key}
                  data-is-set={row.is_set}
                  data-is-secret={row.is_secret}
                  className="border-b border-slate-50 last:border-b-0"
                >
                  <td className="w-1/3 px-2 py-1 align-top text-slate-800">
                    <Identifier>{row.key}</Identifier>
                  </td>
                  <td className="px-2 py-1 align-top text-slate-700">
                    <EnvValueCell row={row} />
                    <p className="mt-0.5 text-[10px] text-slate-500">
                      {/* Round 16 I10 -- catalogued; the server's words
                          stand where the catalogue has none. */}
                      {catalogued(
                        t,
                        `settingsCatalogue.env.${row.key}`,
                        row.description,
                      )}
                    </p>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </section>
  );
}

function EnvValueCell({ row }: { row: SettingsEnvRow }) {
  const { t } = useTranslation();
  if (row.is_secret) {
    return row.is_set ? (
      <span
        data-testid="settings-env-secret-configured"
        className="rounded border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-900"
      >
        {t("screens.settingsAdminView.configured")}
      </span>
    ) : (
      <span
        data-testid="settings-env-secret-unset"
        className="rounded border border-slate-300 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-600"
      >
        {t("screens.settingsAdminView.notSet")}
      </span>
    );
  }
  if (row.key === "MARSAD_API_DOCS_ENABLED") {
    /* Round 16 K11 -- this row's description says how the hosted
       deployment runs (0), and on the host the variable had never
       arrived: Settings read "not set -- the code's default applies"
       beside a description of the explorer as off, while it answered
       on the public address. The code's default is ON, so say what is
       actually being done. */
    const off =
      row.is_set &&
      ["0", "false", "no", "off"].includes((row.value ?? "").trim().toLowerCase());
    return (
      <span
        data-testid="settings-env-api-docs"
        data-served={off ? "false" : "true"}
        className={off ? "text-slate-700" : "font-semibold text-amber-800"}
      >
        {off
          ? t("screens.settingsAdminView.apiDocsOff")
          : t("screens.settingsAdminView.apiDocsServed")}
      </span>
    );
  }
  if (!row.is_set) {
    /* Round 12 G2 -- "not set" is half an answer. What an operator needs
       to know is that the CODE'S OWN DEFAULT is in force, which is a
       different thing from the setting being absent: fifty-seven of
       these reached nothing at all and thirty-four of them rendered
       here with a value, which is the state this wording exists to make
       distinguishable. */
    return (
      <span data-testid="settings-env-unset" className="italic text-slate-400">
        {t("screens.settingsAdminView.notSetDefaultApplies")}
      </span>
    );
  }
  return (
    <span data-testid="settings-env-value" className="font-mono text-slate-900">
      {row.value ?? "—"}
    </span>
  );
}

// P179: operator-facing signing posture -- which algorithm/key actually
// signs dossiers, answer packs + RFI bundles, with a sentinel warning.
function ProofSigningPanel() {
  const { t } = useTranslation();
  const status = useQuery<ProofSigningStatus>({
    queryKey: ["admin", "proof-signing-status"],
    queryFn: () => api.get<ProofSigningStatus>("/v1/admin/proof/signing-status"),
  });
  if (status.isLoading || status.isError || !status.data) return null;
  const s = status.data;
  // Round 19 I24 -- the three postures were component literals.
  const pill = s.is_ed25519
    ? {
        cls: "bg-emerald-100 text-emerald-800",
        label: t("screens.settingsAdminView.signing.ed25519"),
      }
    : s.is_sentinel
      ? {
          cls: "bg-amber-100 text-amber-900",
          label: t("screens.settingsAdminView.signing.sentinel"),
        }
      : {
          cls: "bg-slate-100 text-slate-700",
          label: t("screens.settingsAdminView.signing.hmac"),
        };
  return (
    <section
      data-testid="proof-signing-panel"
      className="rounded-lg border border-slate-200 p-4"
    >
      <h2 className="text-sm font-semibold text-slate-900">
        {t("screens.settingsAdminView.proofSigning")}
      </h2>
      <p className="mt-1 text-xs text-slate-600">
        {t("screens.settingsAdminView.howDossiersAnswerPacksAndRfiBundles")}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
        <span
          data-testid="proof-signing-pill"
          data-ed25519={s.is_ed25519}
          data-sentinel={s.is_sentinel}
          className={`rounded px-2 py-1 font-medium ${pill.cls}`}
        >
          {pill.label}
        </span>
        <span className="rounded bg-slate-100 px-2 py-1 text-slate-700">
          {t("screens.settingsAdminView.keyId")} <Identifier>{s.key_id}</Identifier>
        </span>
        <span className="rounded bg-slate-100 px-2 py-1 text-slate-700">
          {t("screens.settingsAdminView.publishedKeys")} {s.published_key_count}
        </span>
        {s.require_ed25519 && (
          <span className="rounded bg-emerald-100 px-2 py-1 text-emerald-800">
            {t("screens.settingsAdminView.ed25519Required")}
          </span>
        )}
      </div>
      {s.is_sentinel && (
        <p data-testid="proof-signing-warning" className="mt-2 text-xs text-amber-900">
          {t("screens.settingsAdminView.thisInstanceSignsWithTheDefaultSentinel")}
          <code>{t("screens.settingsAdminView.scriptsGenProofKeyPy")}</code>
          {t("screens.settingsAdminView.setThePrivateSeedInEnvAnd")}{" "}
          <code>{t("screens.settingsAdminView.marsadProofRequireEd255191")}</code>{" "}
          {t("screens.settingsAdminView.beforeIssuingArtefactsToARegulator")}
        </p>
      )}
      {/* D2 -- a deployment signing with the sentinel key is issuing
          artefacts a regulator cannot attribute to the bank. The panel
          said so and named nobody to tell. */}
      {status.data?.is_sentinel && (
        <EscalationLine
          testid="proof-signing-escalate"
          who={t("escalate.signingKey")}
          details={{
            what: t("escalate.whatSentinelKey"),
            context: {
              Algorithm: status.data?.algorithm ?? "unknown",
              "Key id": status.data?.key_id ?? "none",
              "Ed25519 required": String(status.data?.require_ed25519 ?? false),
            },
          }}
        />
      )}
      <p className="mt-2 text-xs text-slate-500">
        {t("screens.settingsAdminView.publishedPublicKeys")}{" "}
        <a
          href="/v1/proof/public-keys"
          target="_blank"
          rel="noopener noreferrer"
          className="text-brand-brassDark hover:underline"
        >
          {t("screens.settingsAdminView.v1ProofPublicKeys")}
        </a>
      </p>
    </section>
  );
}

function EmailDeliveryPanel() {
  const { t } = useTranslation();
  const health = useQuery<EmailHealth>({
    queryKey: ["admin", "email-health"],
    queryFn: () => api.get<EmailHealth>("/v1/admin/notifications/email-health"),
  });
  if (health.isLoading || health.isError || !health.data) return null;
  const h = health.data;
  return (
    <section
      data-testid="email-delivery-panel"
      className="rounded-lg border border-slate-200 p-4"
    >
      <h2 className="text-sm font-semibold text-slate-900">
        {t("screens.settingsAdminView.emailDelivery")}
      </h2>
      <div className="mt-2 flex flex-wrap gap-2 text-xs">
        <span className="rounded bg-slate-100 px-2 py-1 text-slate-700">
          {t("screens.settingsAdminView.pending")} {h.pending}
        </span>
        <span className="rounded bg-indigo-100 px-2 py-1 text-indigo-800">
          {t("screens.settingsAdminView.digestHeld")} {h.digest_held}
        </span>
        <span
          data-testid="email-failed-count"
          className={`rounded px-2 py-1 ${
            h.failed > 0 ? "bg-rose-100 text-rose-800" : "bg-slate-100 text-slate-700"
          }`}
        >
          {t("screens.settingsAdminView.failed")} {h.failed}
        </span>
      </div>
      {/* Round 10 F6 -- what the three numbers mean, between the
          numbers and the person to tell. The panel went straight from
          "Pending: 20" to "Escalate to: whoever operates the bank's
          mail relay", and an administrator reading it could not tell
          whether twenty pending was a queue moving or a queue stuck.
          Four rounds. */}
      <p data-testid="email-delivery-reading" className="mt-2 text-xs text-slate-600">
        {h.failed > 0
          ? t("screens.settingsAdminView.readingFailed", { count: h.failed })
          : h.pending > 0
            ? t("screens.settingsAdminView.readingPending", { count: h.pending })
            : t("screens.settingsAdminView.readingClear")}
      </p>
      {h.recent_failures.length > 0 && (
        <ul className="mt-2 space-y-1 text-xs text-rose-700">
          {h.recent_failures.slice(0, 5).map((f) => (
            <li key={f.id}>
              <span className="font-medium">{f.title}</span> — {f.error}{" "}
              {t("screens.settingsAdminView.attempt")} {f.attempts})
            </li>
          ))}
        </ul>
      )}
      {/* D2 -- mail that is not being delivered is somebody's job, and
          this panel named nobody. The relay is the bank's, so its
          operator is who to tell -- not us. */}
      {/* C1 -- second round. The panel is three chips ("Pending: 54")
          with no owner, four lines below another escalation line on the
          same page. It named somebody only on FAILURE, so a backlog
          nobody is draining -- which is what a large pending count is
          -- read as a fact about the weather.

          Who owns the mail relay is true whatever the counts say, so
          the line is not conditional. The failure detail still only
          travels when there is a failure to describe. */}
      <EscalationLine
        testid="email-escalate"
        who={t("escalate.mailRelay")}
        details={{
          what:
            h.failed > 0
              ? "MARSAD notification emails are failing to send"
              : "MARSAD notification email queue",
          context: {
            Failed: h.failed,
            Pending: h.pending,
            "Held for digest": h.digest_held,
            "Most recent error": h.recent_failures[0]?.error ?? "",
          },
        }}
      />
    </section>
  );
}

// P107: flip `bank.lifecycle_stage` back to `fresh` so the Getting
// Started ladder + per-view rails resurface for a new team taking over.
function ResetOnboardingSection() {
  const { t } = useTranslation();
  const patch = useAppSettingPatch();
  const toast = useToast();
  const qc = useQueryClient();
  // Round 14 F4 -- what the stage is now. Resetting a bank that is
  // already fresh changed nothing and still announced a change.
  const settings = useAdminSettings({ enabled: true });
  const rows = Array.isArray(settings.data?.app_settings)
    ? settings.data.app_settings
    : [];
  const alreadyFresh =
    rows.find((r) => r.key === "bank.lifecycle_stage")?.value === "fresh";

  const onReset = () => {
    if (alreadyFresh) {
      toast.push({
        tone: "info",
        message: t("screens.settingsAdminView.onboardingAlreadyFresh"),
      });
      return;
    }
    patch.mutate(
      { key: "bank.lifecycle_stage", value: "fresh" },
      {
        onSuccess: () => {
          void qc.invalidateQueries({ queryKey: onboardingKeys.all });
          toast.push({
            tone: "success",
            // D10 -- it said "visible again" when it had never gone
            // away, which reads as a change that did not happen. What
            // actually moved is the bank's lifecycle stage, and who
            // sees the ladder is a separate question from that.
            message: t("screens.settingsAdminView.onboardingReset"),
            detail: t("screens.settingsAdminView.onboardingResetDetail"),
          });
        },
        onError: (err) => {
          toast.push({
            tone: "error",
            message: t("screens.settingsAdminView.onboardingResetFailed"),
            detail: err.message,
          });
        },
      },
    );
  };

  return (
    <section
      data-testid="settings-reset-onboarding"
      className="flex flex-wrap items-center justify-between gap-3 rounded border border-slate-200 bg-white px-3 py-2"
    >
      <div>
        <p className="text-xs font-semibold text-slate-700">
          {t("screens.settingsAdminView.onboarding")}
        </p>
        <p className="text-xs text-slate-500">
          {t("screens.settingsAdminView.bringBackTheGettingStartedLadderFor")}
        </p>
      </div>
      <button
        type="button"
        data-testid="reset-onboarding-button"
        onClick={onReset}
        disabled={patch.isPending}
        className="rounded border border-brand-brassDark px-3 py-1.5 text-xs font-semibold text-brand-brassDark disabled:opacity-40"
      >
        {patch.isPending
          ? t("screens.settingsAdminView.resetting")
          : t("screens.settingsAdminView.resetOnboarding")}
      </button>
    </section>
  );
}

/**
 * Round 10 A5 -- the settings audit listed every app_settings row in
 * one flat table sorted by key, so a `twin3d.fragility.weight_*` row
 * sat between an IBS admission threshold and an MVB rubric bar with
 * nothing to say which picture it moved. The rows already carried a
 * `category`; nothing read it.
 *
 * The heading is the administrator's language, not the column value:
 * "twin3d" is a table cell, "Twin 3D views" is what an administrator
 * is looking for. A category nobody has written a heading for falls
 * back to its own code rather than disappearing -- a group that
 * vanishes because a label is missing is worse than an ugly one.
 */
/** Round 16 I10 -- the catalogue's sentence where it has one, else the
 *  server's words. Asked with `exists` first: a setting the catalogue
 *  has not met is not a missing key to report. */
function catalogued(t: TFunction, key: string, server: string): string {
  return i18n.exists(key) ? t(key) : server;
}

/** Round 16 I10 -- "Marsad Engine" -> "marsadEngine", the catalogue slug. */
function envGroupSlug(group: string): string {
  return group
    .replace(/-/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((w, i) => (i === 0 ? w.toLowerCase() : w[0]!.toUpperCase() + w.slice(1)))
    .join("");
}

interface AppSettingGroup {
  category: string;
  /** URL fragment the scenes link to. */
  anchor: string;
  /**
   * i18n lookup slug. Kept separate from the category because a
   * category may contain a dot ("roadmap.thresholds") and i18next reads
   * a dot as catalogue nesting -- the heading would silently fall back
   * to the raw column value.
   */
  slug: string | null;
}

const APP_SETTING_GROUPS: AppSettingGroup[] = [
  { category: "twin3d", anchor: "twin3d", slug: "twin3d" },
  { category: "fragility", anchor: "fragility", slug: "fragility" },
  // P416 -- the rubric's bars by regime (Alembic 0241); "ibs_threshold"
  // keeps the CIF bars and the materiality figure.
  {
    category: "ibs_threshold.sama",
    anchor: "ibs-threshold-sama",
    slug: "ibsThresholdSama",
  },
  {
    category: "ibs_threshold.osfi",
    anchor: "ibs-threshold-osfi",
    slug: "ibsThresholdOsfi",
  },
  { category: "ibs_threshold", anchor: "ibs-threshold", slug: "ibsThreshold" },
  { category: "mvb_rubric", anchor: "mvb-rubric", slug: "mvbRubric" },
  { category: "recovery", anchor: "recovery", slug: "recovery" },
  { category: "recommendations", anchor: "recommendations", slug: "recommendations" },
  {
    category: "roadmap.thresholds",
    anchor: "roadmap-thresholds",
    slug: "roadmapThresholds",
  },
  { category: "bank", anchor: "bank", slug: "bank" },
];

function groupAppRows(
  rows: SettingsAppRow[],
): (AppSettingGroup & { rows: SettingsAppRow[] })[] {
  const seen = new Set<string>();
  const out: (AppSettingGroup & { rows: SettingsAppRow[] })[] = [];
  for (const g of APP_SETTING_GROUPS) {
    const bucket = rows.filter((r) => r.category === g.category);
    if (bucket.length === 0) continue;
    seen.add(g.category);
    out.push({ ...g, rows: bucket });
  }
  // A category the catalogue has not met yet keeps its own code rather
  // than disappearing: a group that vanishes for want of a label is
  // worse than an ugly one.
  for (const row of rows) {
    if (seen.has(row.category)) continue;
    seen.add(row.category);
    out.push({
      category: row.category,
      anchor: row.category.replace(/[^a-z0-9]+/gi, "-"),
      slug: null,
      rows: rows.filter((r) => r.category === row.category),
    });
  }
  return out;
}

function AppSettingsSection({ rows }: { rows: SettingsAppRow[] }) {
  const { t } = useTranslation();
  const groups = useMemo(() => groupAppRows(rows), [rows]);
  return (
    <section
      data-testid="settings-app-section"
      className="rounded border border-slate-200 bg-white"
    >
      <header className="border-b border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700">
        {t("screens.settingsAdminView.appSettingsRows")}
        {rows.length})
      </header>
      {rows.length === 0 ? (
        <p
          data-testid="settings-app-empty"
          className="px-3 py-4 text-xs italic text-slate-500"
        >
          {t("screens.settingsAdminView.noAppSettingsRowsYetSeededBy")}
        </p>
      ) : (
        groups.map((group) => (
          <div
            key={group.category}
            id={group.anchor}
            data-testid="settings-app-group"
            data-category={group.category}
            className="scroll-mt-4 border-b border-slate-100 last:border-b-0"
          >
            <div className="bg-slate-50 px-3 py-1">
              <h2 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                {group.slug
                  ? t(`screens.settingsAdminView.appGroup.${group.slug}.title`)
                  : group.category}
              </h2>
              {group.slug && (
                <p className="text-[10px] text-slate-500">
                  {t(`screens.settingsAdminView.appGroup.${group.slug}.blurb`)}
                </p>
              )}
              {group.category === "twin3d" && (
                <Link
                  to="/twin/cascade"
                  data-testid="settings-app-group-back"
                  className="text-[10px] text-brand-brassDark underline decoration-dotted underline-offset-2"
                >
                  {t("screens.settingsAdminView.appGroup.twin3d.back")}
                </Link>
              )}
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-200 bg-white text-start">
                  <th className="px-2 py-1 font-semibold text-slate-600">
                    {t("screens.settingsAdminView.key")}
                  </th>
                  <th className="px-2 py-1 font-semibold text-slate-600">
                    {t("screens.settingsAdminView.type")}
                  </th>
                  <th className="px-2 py-1 font-semibold text-slate-600">
                    {t("screens.settingsAdminView.value")}
                  </th>
                  <th className="px-2 py-1 font-semibold text-slate-600">
                    {t("screens.settingsAdminView.default")}
                  </th>
                  <th className="px-2 py-1 font-semibold text-slate-600">
                    {t("screens.settingsAdminView.by")}
                  </th>
                  <th className="px-2 py-1 font-semibold text-slate-600" />
                </tr>
              </thead>
              <tbody>
                {group.rows.map((row) => (
                  <AppSettingRow key={row.id} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}
    </section>
  );
}

// P43.2: inline-editable row. Clicking the pencil swaps the value
// cell for a type-appropriate input + Save/Cancel.
// P44.2: "History" button expands an inline timeline below the row.
function AppSettingRow({ row }: { row: SettingsAppRow }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [draft, setDraft] = useState<string>(() => formatAppValue(row.value));
  const patch = useAppSettingPatch();
  const toast = useToast();

  const onCancel = () => {
    setEditing(false);
    setDraft(formatAppValue(row.value));
  };

  const onSave = () => {
    const parsed = parseDraft(row.data_type, draft);
    patch.mutate(
      { key: row.key, value: parsed },
      {
        onSuccess: (next) => {
          setEditing(false);
          setDraft(formatAppValue(next.value));
          toast.push({
            tone: "success",
            message: `Updated ${row.key}`,
            detail: `Now: ${formatAppValue(next.value)}`,
          });
        },
        onError: (err) => {
          toast.push({
            tone: "error",
            message: `Update failed: ${row.key}`,
            detail: err.message,
          });
        },
      },
    );
  };

  return (
    <>
      <tr
        data-testid="settings-app-row"
        data-app-key={row.key}
        className="border-b border-slate-50 last:border-b-0"
      >
        <td className="px-2 py-1 align-top font-mono text-slate-800">
          {row.key}
          {row.description && (
            <p className="mt-0.5 text-[10px] font-normal text-slate-500">
              {catalogued(
                t,
                `settingsCatalogue.app.${row.key.replace(/\./g, "__")}`,
                row.description,
              )}
            </p>
          )}
        </td>
        <td className="px-2 py-1 align-top text-slate-600">{row.data_type}</td>
        <td className="px-2 py-1 align-top font-mono text-slate-900">
          {editing ? (
            <EditorInput dataType={row.data_type} value={draft} onChange={setDraft} />
          ) : (
            formatAppValue(row.value)
          )}
        </td>
        <td className="px-2 py-1 align-top font-mono text-slate-500">
          {formatAppValue(row.default_value)}
        </td>
        <td className="px-2 py-1 align-top text-[10px] text-slate-500">
          {row.updated_by_username ?? (
            <span className="italic text-slate-400">
              {t("screens.settingsAdminView.seed")}
            </span>
          )}
        </td>
        <td className="px-2 py-1 align-top">
          {editing ? (
            <div className="flex gap-1">
              <button
                type="button"
                data-testid="settings-app-save"
                disabled={patch.isPending}
                onClick={onSave}
                className="rounded border border-emerald-400 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-900 disabled:opacity-50"
              >
                {patch.isPending
                  ? t("screens.settingsAdminView.saving")
                  : t("screens.settingsAdminView.save")}
              </button>
              <button
                type="button"
                data-testid="settings-app-cancel"
                onClick={onCancel}
                className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-700"
              >
                {t("screens.settingsAdminView.cancel")}
              </button>
            </div>
          ) : (
            <div className="flex gap-1">
              <button
                type="button"
                data-testid="settings-app-edit"
                onClick={() => setEditing(true)}
                className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-700"
              >
                {t("screens.settingsAdminView.edit")}
              </button>
              <button
                type="button"
                data-testid="settings-app-history-toggle"
                data-history-open={historyOpen}
                onClick={() => setHistoryOpen((v) => !v)}
                className="rounded border border-indigo-300 bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-900"
              >
                {historyOpen
                  ? t("screens.settingsAdminView.hideHistory")
                  : t("screens.settingsAdminView.history")}
              </button>
            </div>
          )}
        </td>
      </tr>
      {historyOpen && (
        <tr data-testid="settings-app-history-row">
          <td colSpan={6} className="bg-slate-50 px-3 py-2">
            <AppSettingHistoryTimeline appKey={row.key} />
          </td>
        </tr>
      )}
    </>
  );
}

// P44.2: vertical timeline of every change to one key.
function AppSettingHistoryTimeline({ appKey }: { appKey: string }) {
  const { t } = useTranslation();
  const history = useAppSettingHistory(appKey, { limit: 50 });
  if (history.isLoading) {
    return (
      <p
        data-testid="settings-app-history-loading"
        className="text-[11px] italic text-slate-500"
      >
        {t("screens.settingsAdminView.loadingHistory")}
      </p>
    );
  }
  if (history.error) {
    return (
      <p data-testid="settings-app-history-error" className="text-[11px] text-rose-700">
        {t("screens.settingsAdminView.failedToLoadHistory")}{" "}
        {history.error instanceof Error
          ? history.error.message
          : t("screens.settingsAdminView.unknown")}
      </p>
    );
  }
  const rows = history.data ?? [];
  if (rows.length === 0) {
    return (
      <p
        data-testid="settings-app-history-empty"
        className="text-[11px] italic text-slate-500"
      >
        {t("screens.settingsAdminView.noChangesYetTheFirstEditWill")}
      </p>
    );
  }
  return (
    <ol
      data-testid="settings-app-history-timeline"
      className="space-y-2 border-s-2 border-indigo-200 ps-3"
    >
      {rows.map((entry, idx) => (
        <HistoryEntry
          key={entry.row_hash_hex}
          entry={entry}
          isLatest={idx === 0}
          previous={rows[idx + 1] ?? null}
        />
      ))}
    </ol>
  );
}

function HistoryEntry({
  entry,
  isLatest,
  previous,
}: {
  entry: AppSettingHistoryRow;
  isLatest: boolean;
  previous: AppSettingHistoryRow | null;
}) {
  const { t } = useTranslation();
  const when = formatDateTime(entry.captured_at, i18n.language);
  const currentFormatted = formatAppValue(entry.value);
  const isGenesis = previous === null;
  return (
    <li
      data-testid="settings-app-history-entry"
      data-row-hash={entry.row_hash_hex}
      data-is-genesis={isGenesis}
      className="text-[11px]"
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
        <span className="font-mono text-slate-800">{when}</span>
        {isGenesis ? (
          <span className="font-semibold text-slate-900">
            {t("screens.settingsAdminView.value2")}{" "}
            <span data-testid="settings-app-history-value" className="font-mono">
              {currentFormatted}
            </span>
          </span>
        ) : (
          <span className="font-semibold text-slate-900">
            <span
              data-testid="settings-app-history-before"
              className="font-mono text-slate-500 line-through"
            >
              {formatAppValue(previous.value)}
            </span>
            <span className="mx-1 text-slate-500" aria-hidden>
              {t("screens.settingsAdminView.rarr")}
            </span>
            <span data-testid="settings-app-history-after" className="font-mono">
              {currentFormatted}
            </span>
          </span>
        )}
        {isLatest && (
          <span
            data-testid="settings-app-history-latest-pill"
            className="rounded border border-emerald-300 bg-emerald-50 px-1.5 py-0 text-[9px] font-semibold text-emerald-900"
          >
            {t("screens.settingsAdminView.latest")}
          </span>
        )}
        {isGenesis && (
          <span
            data-testid="settings-app-history-genesis-pill"
            className="rounded border border-slate-300 bg-slate-100 px-1.5 py-0 text-[9px] font-semibold text-slate-600"
          >
            {t("screens.settingsAdminView.genesis")}
          </span>
        )}
      </div>
      <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-[10px] text-slate-500">
        <span>
          {t("screens.settingsAdminView.by2")}{" "}
          <span className="text-slate-700">
            {entry.updated_by_username ?? t("screens.settingsAdminView.unknown2")}
          </span>
        </span>
        <span>{entry.captured_by}</span>
        <span
          title={`row_hash ${entry.row_hash_hex}`}
          className="font-mono text-slate-400"
        >
          {entry.row_hash_hex.slice(0, 12)}...
        </span>
      </div>
    </li>
  );
}

// Type-appropriate input. integer/decimal -> numeric; boolean ->
// select; string/json -> text. JSON edits live in the same string
// field; backend round-trips through json.loads.
function EditorInput({
  dataType,
  value,
  onChange,
}: {
  dataType: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const { t } = useTranslation();
  if (dataType === "boolean") {
    return (
      <select
        data-testid="settings-app-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded border border-slate-300 px-2 py-0.5 text-xs"
      >
        <option value="true">{t("screens.settingsAdminView.true")}</option>
        <option value="false">{t("screens.settingsAdminView.false")}</option>
      </select>
    );
  }
  if (dataType === "json") {
    return (
      <textarea
        dir="auto"
        data-testid="settings-app-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="block w-64 rounded border border-slate-300 px-2 py-0.5 font-mono text-[11px]"
      />
    );
  }
  const inputMode =
    dataType === "integer" || dataType === "decimal" ? "decimal" : undefined;
  return (
    <input
      dir="auto"
      data-testid="settings-app-input"
      type="text"
      inputMode={inputMode}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded border border-slate-300 px-2 py-0.5 text-xs"
    />
  );
}

function parseDraft(dataType: string, draft: string): unknown {
  if (dataType === "boolean") return draft === "true";
  if (dataType === "integer") {
    const n = Number(draft);
    return Number.isInteger(n) ? n : draft;
  }
  if (dataType === "decimal") return draft;
  return draft;
}

// ── Webhook endpoints (P345) ────────────────────────────────────────

interface WebhookRow {
  id: string;
  name: string;
  url: string;
  kind_prefix: string | null;
  has_secret: boolean;
  enabled: boolean;
  created_at: string;
}

function WebhooksPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const hooks = useQuery<WebhookRow[]>({
    queryKey: ["admin", "notification-webhooks"],
    queryFn: () => api.get<WebhookRow[]>("/v1/admin/notifications/webhooks"),
  });
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [kindPrefix, setKindPrefix] = useState("");
  const [secret, setSecret] = useState("");
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["admin", "notification-webhooks"] });
  const create = useMutation({
    mutationFn: () =>
      api.post("/v1/admin/notifications/webhooks", {
        name,
        url,
        kind_prefix: kindPrefix || null,
        secret: secret || null,
      }),
    onSuccess: () => {
      setName("");
      setUrl("");
      setKindPrefix("");
      setSecret("");
      void invalidate();
    },
  });
  const toggle = useMutation({
    mutationFn: (row: WebhookRow) =>
      api.patch(`/v1/admin/notifications/webhooks/${row.id}`, {
        enabled: !row.enabled,
      }),
    onSuccess: () => void invalidate(),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/admin/notifications/webhooks/${id}`),
    onSuccess: () => void invalidate(),
  });

  if (hooks.isLoading || hooks.isError) return null;
  return (
    <section
      data-testid="webhooks-panel"
      className="rounded-lg border border-slate-200 p-4"
    >
      <h2 className="mb-1 text-sm font-semibold text-slate-800">
        {t("screens.settingsAdminView.webhookEndpoints")}
      </h2>
      <p className="mb-3 text-xs text-slate-500">
        {t("screens.settingsAdminView.outboundJsonOnEveryMatchingNotificationMs")}
      </p>
      <ul className="mb-3 space-y-1">
        {(hooks.data ?? []).map((h) => (
          <li
            key={h.id}
            data-testid={`webhook-row-${h.name}`}
            className="flex flex-wrap items-baseline gap-x-3 text-xs"
          >
            <span className="font-medium text-slate-900">{h.name}</span>
            <span className="font-mono text-slate-500">{h.url}</span>
            {h.kind_prefix && (
              <span className="text-slate-500">
                {t("screens.settingsAdminView.kinds")} {h.kind_prefix}*
              </span>
            )}
            {h.has_secret && (
              <span className="text-emerald-700">
                {t("screens.settingsAdminView.signed")}
              </span>
            )}
            <button
              type="button"
              onClick={() => toggle.mutate(h)}
              className="text-brand-brassDark hover:underline"
            >
              {h.enabled
                ? t("screens.settingsAdminView.disable")
                : t("screens.settingsAdminView.enable")}
            </button>
            <button
              type="button"
              onClick={() => remove.mutate(h.id)}
              className="text-red-600 hover:underline"
            >
              {t("screens.settingsAdminView.delete")}
            </button>
            {!h.enabled && (
              <span className="text-amber-700">
                {t("screens.settingsAdminView.disabled")}
              </span>
            )}
          </li>
        ))}
        {(hooks.data ?? []).length === 0 && (
          <li className="text-xs text-slate-400">
            {t("screens.settingsAdminView.noEndpointsRegistered")}
          </li>
        )}
      </ul>
      {/* Round 12 H3 -- accessibility was measured for the first time
          this round and came back better than anyone expected: 501
          controls across nine screens and not one of them unnamed.
          These four were the exception, and they are on the one form
          on this page that WRITES anything. A placeholder is not an
          accessible name: it is announced as a hint where it is
          announced at all, and it disappears the moment the field has
          a value, so a screen-reader user filling the third box cannot
          find out what the first one was. Two of them also carried
          hard-coded English. */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          dir="auto"
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-label={t("screens.settingsAdminView.webhookName")}
          placeholder={t("screens.settingsAdminView.webhookName")}
          data-testid="webhook-name"
          className="rounded border border-slate-300 px-2 py-1 text-xs"
        />
        <input
          dir="auto"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          aria-label={t("screens.settingsAdminView.webhookUrl")}
          placeholder="https://..."
          data-testid="webhook-url"
          className="w-64 rounded border border-slate-300 px-2 py-1 text-xs"
        />
        <input
          dir="auto"
          value={kindPrefix}
          onChange={(e) => setKindPrefix(e.target.value)}
          aria-label={t("screens.settingsAdminView.kindPrefixOptional")}
          placeholder={t("screens.settingsAdminView.kindPrefixOptional")}
          className="rounded border border-slate-300 px-2 py-1 text-xs"
        />
        <input
          dir="auto"
          value={secret}
          onChange={(e) => setSecret(e.target.value)}
          aria-label={t("screens.settingsAdminView.webhookSecret")}
          placeholder={t("screens.settingsAdminView.webhookSecret")}
          className="rounded border border-slate-300 px-2 py-1 text-xs"
        />
        <button
          type="button"
          onClick={() => create.mutate()}
          disabled={!name || !url || create.isPending}
          data-testid="webhook-add"
          className="rounded bg-brand-brass px-2 py-1 text-xs font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
        >
          {t("screens.settingsAdminView.addEndpoint")}
        </button>
      </div>
    </section>
  );
}
